import { css, StyleSheet } from 'aphrodite';
import * as React from 'react';
import { IKeepInTouchScore } from '../../../../../../../models/KeepInTouchAgent';
import { useKeepInTouchScoresByContact } from '../../../../../../../queries/KeepInTouchAgent';
import { useUserSession } from '../../../../../../../models/hooks/appStateHooks';
import { ChevronDown } from '../../../../../../components/svgs/icons/ChevronDown';
import { ChevronUp } from '../../../../../../components/svgs/icons/ChevronUp';
import { borderColor, grayIconFill, white } from '../../../../../../styles/colors';

function getScoreColor(score: number): string {
	if (score >= 90) return '#dc2626';
	if (score >= 70) return '#ea580c';
	if (score >= 50) return '#ca8a04';
	if (score >= 30) return '#2563eb';
	return '#6b7280';
}

interface KeepInTouchAgentScoreProps {
	contactId: string;
}

export const KeepInTouchAgentScore: React.FC<KeepInTouchAgentScoreProps> = ({ contactId }) => {
	const userSession = useUserSession();
	const featureEnabled = !!userSession.account.features?.keepInTouch?.enabled;
	const { data: scores } = useKeepInTouchScoresByContact({ contactId, enabled: featureEnabled });
	const [expanded, setExpanded] = React.useState(false);

	if (!featureEnabled || !scores?.length) return null;

	// Show the highest score
	const sorted = [...scores].sort((a, b) => b.score - a.score);
	const topScore = sorted[0];

	return (
		<div className={css(s.container)}>
			<button className={css(s.row)} onClick={() => setExpanded(!expanded)} type='button'>
				<div className={css(s.scoreBadge)} style={{ backgroundColor: getScoreColor(topScore.score) }}>
					{topScore.score}
				</div>
				<span className={css(s.label)}>Keep In Touch Score</span>
				<span className={css(s.chevron)}>
					{expanded ? <ChevronUp width={16} height={16} /> : <ChevronDown width={16} height={16} />}
				</span>
			</button>
			{expanded ? (
				<div className={css(s.details)}>
					{sorted.map((score: IKeepInTouchScore) => (
						<div key={score.id} className={css(s.scoreDetail)}>
							{sorted.length > 1 ? (
								<div className={css(s.scoreHeader)}>
									<div className={css(s.miniScoreBadge)} style={{ backgroundColor: getScoreColor(score.score) }}>
										{score.score}
									</div>
								</div>
							) : null}
							{score.reasoning ? (
								<div className={css(s.detailSection)}>
									<div className={css(s.detailLabel)}>Reasoning</div>
									<p className={css(s.detailText)}>{score.reasoning}</p>
								</div>
							) : null}
							{score.suggestedOutreach ? (
								<div className={css(s.detailSection)}>
									<div className={css(s.detailLabel)}>Suggested Outreach</div>
									<p className={css(s.detailText)}>{score.suggestedOutreach}</p>
								</div>
							) : null}
							{score.webFindings ? (
								<div className={css(s.detailSection)}>
									<div className={css(s.detailLabel)}>Web Findings</div>
									<p className={css(s.detailText)}>{score.webFindings}</p>
								</div>
							) : null}
						</div>
					))}
				</div>
			) : null}
		</div>
	);
};

const s = StyleSheet.create({
	container: {
		marginTop: 8,
	},
	row: {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
		width: '100%',
		padding: '6px 0',
		background: 'none',
		border: 'none',
		cursor: 'pointer',
		textAlign: 'left',
		fontSize: 14,
	},
	scoreBadge: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: 28,
		height: 28,
		borderRadius: '50%',
		fontSize: 12,
		fontWeight: 700,
		color: white,
		flexShrink: 0,
	},
	label: {
		flex: 1,
		fontSize: 14,
	},
	chevron: {
		color: grayIconFill,
		display: 'inline-flex',
		alignItems: 'center',
	},
	details: {
		padding: '8px 0 0 36px',
	},
	scoreDetail: {
		borderTop: `1px solid ${borderColor}`,
		paddingTop: 8,
		marginBottom: 8,
		':first-child': {
			borderTop: 'none',
			paddingTop: 0,
		},
	},
	scoreHeader: {
		marginBottom: 4,
	},
	miniScoreBadge: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: 22,
		height: 22,
		borderRadius: '50%',
		fontSize: 10,
		fontWeight: 700,
		color: white,
	},
	detailSection: {
		marginBottom: 6,
	},
	detailLabel: {
		fontSize: 11,
		fontWeight: 600,
		textTransform: 'uppercase' as const,
		color: grayIconFill,
		marginBottom: 2,
	},
	detailText: {
		fontSize: 13,
		lineHeight: 1.4,
		margin: 0,
		whiteSpace: 'pre-wrap',
	},
});
